import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Circle, FileText, LogOut, MessageSquarePlus,
  RefreshCcw, Sparkles, ChevronRight, Download,
} from "lucide-react";

import { testApi, setTestToken, getTestToken, recordEvent } from "@/lib/testApi";

/**
 * The Test Workspace has been simplified around one clear question:
 *
 *   Should the family office invest €20M in this opportunity?
 *
 * A qualified user reaches the central value in under five minutes through
 * four consequential actions. Deeper analytical surfaces remain available on
 * demand but do not gate the primary journey.
 */

import CompanyWorkspace from "@/pages/CompanyWorkspace";

const FOUR_STEP_JOURNEY = [
  { key: "material-issues", label: "Review the three issues that matter most" },
  { key: "open-evidence",   label: "Open the evidence behind one issue" },
  { key: "approve",         label: "Approve the fictional investment with two conditions" },
  { key: "passport",        label: "See conditions become monitoring triggers" },
];

const SURFACES = [
  { key: "decision",   label: "Decision" },
  { key: "evidence",   label: "Evidence" },
  { key: "review",     label: "Review" },
  { key: "record",     label: "Decision Passport" },
  { key: "monitoring", label: "Monitoring" },
];

export default function DemoWorkspace() {
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [me, setMe] = useState(null);
  const [surface, setSurface] = useState("decision");
  const [loading, setLoading] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => { document.title = "ImpactVest Test Workspace"; }, []);

  const load = useCallback(async () => {
    try {
      const [w, u] = await Promise.all([testApi.get("/test/workspace"), testApi.get("/test/me")]);
      setWorkspace(w.data.workspace); setMe(u.data.user);
      recordEvent("sample_decision_started");
    } catch {
      toast.error("Session expired.");
      setTestToken(null);
      // P0 pathway routing: redirect to /access without a stale ?path=
      // query. The picker will render and let the user re-declare intent.
      navigate("/access", { replace: true });
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!getTestToken()) {
      // Legacy /app/demo behavior for unauthenticated users: send them to
      // the neutral /access picker. Never silently route into either
      // pathway based on session storage or component defaults.
      navigate("/access", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const journeyDone = useMemo(() => new Set(workspace?.journey_progress || []), [workspace]);

  const markStep = async (k) => {
    if (journeyDone.has(k)) return;
    try {
      await testApi.post(`/test/workspace/journey/${k}`);
      setWorkspace((w) => ({ ...w, journey_progress: [...(w.journey_progress || []), k] }));
    } catch {}
  };

  const gotoSurface = (s) => {
    setSurface(s);
    const map = { decision: "opportunity_brief_viewed", evidence: "evidence_map_viewed",
                  review: "open_question_reviewed", record: "decision_record_reviewed",
                  monitoring: "monitoring_plan_viewed" };
    if (map[s]) recordEvent(map[s]);
  };

  const doReset = async () => {
    try {
      const r = await testApi.post("/test/workspace/reset");
      setWorkspace(r.data.workspace); toast.success("Test workspace reset.");
      setSurface("decision");
    } catch { toast.error("Unable to reset."); }
  };

  const doExport = async () => {
    try {
      const r = await testApi.get("/test/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `impactvest-passport-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Unable to export."); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-iv-bg">
      <span className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading test workspace…</span>
    </div>
  );
  if (!workspace) return null;
  // WP-G · Pathway branching. A company-pathway user receives a
  // substantively different workflow — evidence intake, claim register,
  // substantiation review, claim strengthening, readiness actions and a
  // versioned Investor Evidence Pack. Investor-pathway users continue on
  // the Decision Passport journey below.
  if (me?.path_intent === "company") {
    return <CompanyWorkspace me={me} onSignOut={() => { setTestToken(null); navigate("/"); }} />;
  }
  const opp = workspace.opportunity;

  return (
    <div className="min-h-screen bg-iv-bg">
      {/* Watermark bar */}
      <div className="border-b border-[var(--ivc-line-warm)] bg-[var(--ivc-bg)] text-[var(--ivc-ivory-2)]">
        <div className="iv-container flex items-center justify-between h-[36px]">
          <span className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--ivc-gold-2)]">
            Test Workspace · Fictional Data
          </span>
          <div className="flex items-center gap-4">
            <button onClick={doReset} className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] hover:text-[var(--ivc-ivory)]" data-testid="demo-reset">
              <RefreshCcw size={12} /> Reset
            </button>
            <button onClick={doExport} className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] hover:text-[var(--ivc-ivory)]" data-testid="demo-export">
              <Download size={12} /> Export (fictional)
            </button>
            <button onClick={() => { setTestToken(null); navigate("/"); }} className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] hover:text-[var(--ivc-ivory)]" data-testid="demo-signout">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="border-b border-iv-line bg-iv-surface sticky top-0 z-40">
        <div className="iv-container flex items-center justify-between h-[60px]">
          <div className="flex items-center gap-4 min-w-0">
            <span className="font-serif-display text-[18px] text-iv-fg truncate">{opp.name}</span>
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.18em] uppercase text-iv-fg-3">
              {opp.investment_amount} · {opp.stage}
            </span>
          </div>
          <div className="flex items-center gap-1 overflow-x-auto">
            {SURFACES.map((s) => (
              <button key={s.key} onClick={() => gotoSurface(s.key)} data-testid={`demo-tab-${s.key}`}
                className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${surface === s.key ? "border-iv-green text-iv-fg" : "border-transparent text-iv-fg-3 hover:text-iv-fg"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <Link to="/access" onClick={() => recordEvent("live_opportunity_application_started")} className="iv-btn iv-btn--ghost" data-testid="demo-activate-live">
            Use with a live opportunity <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>

      <div className="iv-container py-6 md:py-10 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {surface === "decision"   && <DecisionSurface opp={opp} markStep={markStep} gotoSurface={gotoSurface} />}
          {surface === "evidence"   && <EvidenceSurface opp={opp} markStep={markStep} />}
          {surface === "review"     && <ReviewSurface opp={opp} markStep={markStep} />}
          {surface === "record"     && <PassportSurface opp={opp} refresh={load} setSurface={gotoSurface} markStep={markStep} onOpenFeedback={() => setFeedbackOpen(true)} />}
          {surface === "monitoring" && <MonitoringSurface opp={opp} markStep={markStep} />}
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-6">
          <TourPanel journey={journeyDone} />
          <ActivatePanel />
        </aside>
      </div>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}

/* ============================================================ TOUR PANEL */

function TourPanel({ journey }) {
  const done = FOUR_STEP_JOURNEY.filter((s) => journey.has(s.key)).length;
  return (
    <div className="iv-canvas rounded-[3px] p-6" data-testid="demo-tour">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-[var(--ivc-gold-2)]">
          Sample decision · Four actions
        </span>
        <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">
          {done} of {FOUR_STEP_JOURNEY.length}
        </span>
      </div>
      <ol className="space-y-2.5">
        {FOUR_STEP_JOURNEY.map((s, i) => {
          const isDone = journey.has(s.key);
          const Icon = isDone ? CheckCircle2 : Circle;
          return (
            <li key={s.key} className="flex items-start gap-3">
              <Icon size={15} className={isDone ? "text-[var(--ivc-forest)] mt-0.5" : "text-[var(--ivc-ivory-4)] mt-0.5"} strokeWidth={1.4} />
              <div>
                <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)] mr-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[13px] leading-snug text-[var(--ivc-ivory)]">{s.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ActivatePanel() {
  return (
    <div className="iv-panel p-6" data-testid="demo-activate-panel">
      <div className="iv-eyebrow mb-3">Try with your own documents</div>
      <h4 className="font-serif-display text-[22px] leading-tight text-iv-fg">Bring PDFs, DOCX, XLSX or CSV.</h4>
      <p className="text-[13.5px] text-iv-fg-2 mt-3 leading-relaxed">
        Create a live opportunity inside your test workspace, upload real diligence materials and let
        ImpactVest extract evidence, classify claims and generate a Decision Passport with grounded
        citations. Fictional demonstration only in this test workspace.
      </p>
      <Link to="/app/demo/opportunities/new" className="iv-btn iv-btn--primary mt-5" data-testid="demo-create-real-opp">
        Create a live opportunity <ArrowRight size={14} />
      </Link>
      <Link to="/access" onClick={() => recordEvent("live_opportunity_application_started")} className="iv-btn iv-btn--ghost mt-3 w-full justify-center" data-testid="demo-activate-live-app">
        Apply to use with confidential materials
      </Link>
    </div>
  );
}

/* ============================================================ DECISION LAYER 1 */

function DecisionSurface({ opp, markStep, gotoSurface }) {
  useEffect(() => { markStep("material-issues"); recordEvent("material_issue_reviewed"); }, []); // eslint-disable-line
  return (
    <div className="space-y-4" data-testid="surface-decision">
      {/* The central question */}
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-8">
        <div className="iv-canvas-eyebrow mb-4">The decision</div>
        <h2 className="font-serif-display text-[30px] md:text-[40px] leading-[1.04] tracking-tight text-[var(--ivc-ivory)] max-w-[36ch]">
          {opp.central_question}
        </h2>
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
          <Meta label="Amount" value={opp.investment_amount_display || opp.investment_amount} />
          <Meta label="Stage" value={opp.stage} />
          <Meta label="Decision deadline" value={opp.decision_deadline?.slice(0, 10)} />
          <Meta label="Hold" value={opp.hold} />
        </div>
        <div className="mt-8 border-l-2 border-[var(--ivc-forest)] pl-6 py-2 bg-[linear-gradient(90deg,rgba(107,178,140,0.07),transparent_70%)]">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-forest)]">
            Current recommendation
          </div>
          <div className="font-serif-display text-[22px] leading-tight mt-2 text-[var(--ivc-ivory)]">
            {opp.current_recommendation}
          </div>
        </div>
        <div className="mt-6 border-l-2 border-[var(--ivc-amber)] pl-6 py-2 bg-[linear-gradient(90deg,rgba(224,169,79,0.05),transparent_70%)]">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-amber)]">
            Most important open question
          </div>
          <div className="font-serif-display text-[18px] leading-snug mt-2 text-[var(--ivc-ivory-2)] italic">
            {opp.most_important_open_question}
          </div>
        </div>
      </div>

      {/* The three material issues */}
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-8" data-testid="material-issues">
        <div className="flex items-baseline justify-between mb-5">
          <div className="iv-canvas-eyebrow">Three material issues</div>
          <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">
            Ordered by significance
          </span>
        </div>
        <ul className="space-y-1">
          {opp.material_issues.map((mi, i) => (
            <li key={mi.id} data-testid={`material-issue-${mi.id}`}
                className="grid grid-cols-[46px_1fr_auto] items-start gap-5 py-4 border-b border-[var(--ivc-line)]">
              <span className="font-mono text-[11px] tracking-[0.18em] text-[var(--ivc-gold-2)] mt-1.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <div className="font-serif-display text-[20px] leading-tight text-[var(--ivc-ivory)]">{mi.title}</div>
                <p className="text-[14px] text-[var(--ivc-ivory-2)] mt-1.5 max-w-2xl leading-relaxed">{mi.summary}</p>
                <div className="mt-2 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                  Significance · {mi.significance} · {mi.resolution_status}
                </div>
              </div>
              <button
                onClick={() => { gotoSurface("evidence"); markStep("open-evidence"); recordEvent("material_issue_reviewed", { issue: mi.id }); }}
                data-testid={`material-issue-open-${mi.id}`}
                className="iv-btn iv-btn--ghost self-center"
              >
                Open evidence <ChevronRight size={14} />
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Immediate next action */}
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="iv-canvas-eyebrow mb-2">Immediate next action</div>
          <p className="text-[15px] text-[var(--ivc-ivory)] max-w-2xl leading-relaxed">{opp.immediate_next_action}</p>
        </div>
        <button
          onClick={() => gotoSurface("record")}
          className="iv-btn iv-btn--primary"
          data-testid="decision-next-action"
        >
          Advance the decision <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">{label}</div>
      <div className="text-[14px] text-[var(--ivc-ivory)] mt-1">{value}</div>
    </div>
  );
}

/* ============================================================ EVIDENCE LAYER 2/3 */

function EvidenceSurface({ opp, markStep }) {
  const [focused, setFocused] = useState(null);
  const claim = focused && opp.claims.find((c) => c.id === focused);
  useEffect(() => { markStep("open-evidence"); }, []); // eslint-disable-line

  const openCitation = (c) => {
    setFocused(c.id); markStep("open-evidence");
    recordEvent("source_citation_opened", { claim_id: c.id });
  };

  const toneMap = { independently_supported: "verified", supplied_evidence: "supported",
                    management_assertion: "assertion", requires_review: "review", contradiction: "review" };

  return (
    <div className="space-y-4" data-testid="surface-evidence">
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
        <div className="iv-canvas-eyebrow mb-3">Evidence · Layer 2</div>
        <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight text-[var(--ivc-ivory)]">
          The evidence behind each claim.
        </h2>
        <p className="text-[14.5px] text-[var(--ivc-ivory-2)] mt-3 max-w-3xl leading-relaxed">
          Select a claim to reveal the document, page reference and relevant excerpt.
          Contradictions and unresolved questions are surfaced with distinct status.
        </p>
      </div>
      <div className="iv-canvas rounded-[3px] px-6 md:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {opp.claims.map((c) => (
            <button key={c.id} onClick={() => openCitation(c)} data-testid={`demo-claim-${c.id}`}
              onMouseEnter={() => { setFocused(c.id); recordEvent("claim_selected", { claim_id: c.id }); }}
              className={`text-left py-3 border-b border-[var(--ivc-line)] transition-opacity ${focused && focused !== c.id ? "opacity-40" : "opacity-100"}`}>
              <div className="flex items-start gap-3">
                <span className={`iv-ev-dot iv-ev-dot--${toneMap[c.status]} mt-1.5`} />
                <div>
                  <p className="text-[14.5px] leading-snug text-[var(--ivc-ivory)]">{c.label}</p>
                  <p className="font-mono text-[10.5px] tracking-[0.10em] uppercase text-[var(--ivc-ivory-3)] mt-1">
                    {c.status.replace(/_/g, " ")} · Materiality {c.materiality}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
        {claim && (
          <div className="mt-6 border-t border-[var(--ivc-line-strong)] pt-6" data-testid="demo-claim-detail">
            <div className="iv-canvas-eyebrow mb-3">Source citations</div>
            {claim.sources.map((s, i) => (
              <div key={i} className="border-l border-[var(--ivc-line-warm)] pl-4 mb-5">
                <div className="font-mono text-[11px] text-[var(--ivc-ivory-2)]">{s.document_name}</div>
                <div className="font-mono text-[10.5px] tracking-[0.10em] text-[var(--ivc-ivory-3)] mt-0.5">{s.page}</div>
                <p className="font-serif-display italic text-[14.5px] text-[var(--ivc-ivory)] mt-2 leading-relaxed">
                  &ldquo;{s.excerpt}&rdquo;
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================ REVIEW */

function ReviewSurface({ opp, markStep }) {
  useEffect(() => { markStep("open-evidence"); }, []); // eslint-disable-line
  return (
    <div className="space-y-4" data-testid="surface-review">
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
        <div className="iv-canvas-eyebrow mb-3">Review · Layer 3</div>
        <h2 className="font-serif-display text-[26px] md:text-[32px] leading-tight text-[var(--ivc-ivory)]">
          Matters requiring judgment.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="iv-canvas rounded-[3px] px-6 py-6">
          <div className="iv-canvas-eyebrow mb-3">Open questions</div>
          <ul className="space-y-3">
            {opp.open_questions.map((q) => (
              <li key={q.id} className="flex items-start gap-3">
                <span className="iv-ev-dot iv-ev-dot--review mt-1.5" />
                <div>
                  <p className="text-[14px] text-[var(--ivc-ivory)]">{q.label}</p>
                  <p className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] mt-1">
                    Priority · {q.priority}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="iv-canvas rounded-[3px] px-6 py-6">
          <div className="iv-canvas-eyebrow mb-3">Material risks</div>
          <ul className="space-y-3">
            {opp.risks.map((r) => (
              <li key={r.id} className="flex items-start gap-3">
                <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase mt-1"
                  style={{ color: r.severity === "Material" ? "var(--ivc-amber)" : r.severity === "Elevated" ? "var(--ivc-gold-2)" : "var(--ivc-ivory-3)" }}>
                  {r.severity}
                </span>
                <span className="text-[13.5px] text-[var(--ivc-ivory-2)] leading-snug">{r.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="iv-canvas rounded-[3px] px-6 py-6">
        <div className="iv-canvas-eyebrow mb-3">Reviewer commentary & dissent</div>
        <ul className="space-y-3">
          {(opp.reviewer_comments || []).map((c) => (
            <li key={c.id} className="border-l border-[var(--ivc-line-warm)] pl-4">
              <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">{c.author}</div>
              <p className="text-[13.5px] text-[var(--ivc-ivory)] mt-1 leading-relaxed">{c.body}</p>
            </li>
          ))}
        </ul>
        {(opp.dissenting_views || []).length > 0 && (
          <>
            <div className="iv-canvas-eyebrow mt-6 mb-3">Dissenting views</div>
            {opp.dissenting_views.map((d) => (
              <div key={d.id} className="border-l border-[rgba(224,169,79,0.45)] pl-4">
                <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-amber)]">{d.author}</div>
                <p className="text-[13.5px] text-[var(--ivc-ivory)] mt-1 leading-relaxed">{d.body}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================ DECISION PASSPORT */

function PassportSurface({ opp, refresh, setSurface, markStep, onOpenFeedback }) {
  const rec = opp.decision_record;
  const [conditions, setConditions] = useState(rec.conditions.slice(0, 2));
  const [newCondition, setNewCondition] = useState("");
  const [approveComment, setApproveComment] = useState("");
  const [passport, setPassport] = useState(null);
  const [passportLoading, setPassportLoading] = useState(false);

  useEffect(() => { markStep("open-evidence"); recordEvent("decision_record_reviewed"); }, []); // eslint-disable-line
  useEffect(() => { setConditions(rec.conditions.slice(0, 2)); }, [rec]);

  const addCondition = () => {
    if (!newCondition.trim()) return;
    setConditions((cs) => [...cs, newCondition.trim()]);
    setNewCondition("");
    recordEvent("condition_added");
  };
  const removeCondition = (i) => setConditions((cs) => cs.filter((_, j) => j !== i));

  const act = async (action, extra = {}) => {
    try {
      await testApi.post("/test/decision-record/action", { action, ...extra });
      await refresh();
      if (action === "request_revision") toast.info("Revision requested.");
      if (action === "approve") {
        markStep("approve"); markStep("passport");
        toast.success("Simulated committee approval recorded.");
        await generatePassport();
        setTimeout(() => setSurface("monitoring"), 900);
        onOpenFeedback();
      } else if (action === "reopen") toast.info("Record reopened.");
      else toast.success(`Recorded: ${action.replace("_", " ")}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Unable to record action."); }
  };

  const generatePassport = async () => {
    setPassportLoading(true);
    try {
      const r = await testApi.get("/test/decision-passport");
      setPassport(r.data);
    } catch { toast.error("Unable to generate passport."); }
    finally { setPassportLoading(false); }
  };

  useEffect(() => {
    if (rec.status === "Approved" && !passport) generatePassport();
  }, [rec.status]); // eslint-disable-line

  const statusTone = { Draft: "iv-chip--gold", "In review": "iv-chip--gold",
    "Revision requested": "iv-chip--gold", Approved: "iv-chip--green",
    Declined: "iv-chip--danger", Deferred: "iv-chip--gold" }[rec.status] || "iv-chip";

  return (
    <div className="space-y-4" data-testid="surface-record">
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="iv-canvas-eyebrow mb-3">Decision Passport · Draft</div>
            <h2 className="font-serif-display text-[28px] md:text-[36px] leading-tight text-[var(--ivc-ivory)]">
              {opp.name}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className={`iv-chip ${statusTone}`}>{rec.status}</span>
            {rec.approvals.length === 0 && (
              <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-gold-2)]">Draft · AI-assisted</span>
            )}
          </div>
        </div>
        <p className="text-[15px] leading-relaxed text-[var(--ivc-ivory-2)] max-w-3xl mt-4">
          {rec.executive_brief}
        </p>
      </div>

      {/* Conditions the user can shape before approval */}
      {rec.status !== "Approved" && (
        <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="passport-conditions">
          <div className="iv-canvas-eyebrow mb-3">Approval conditions · Two required</div>
          <ul className="space-y-2 mb-4">
            {conditions.map((c, i) => (
              <li key={i} className="grid grid-cols-[36px_1fr_auto] items-start gap-3 py-2 border-b border-[var(--ivc-line)]">
                <span className="font-mono text-[10.5px] tracking-[0.18em] text-[var(--ivc-gold-2)] mt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[14px] text-[var(--ivc-ivory)] leading-snug">{c}</span>
                <button onClick={() => removeCondition(i)} className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] hover:text-[var(--ivc-amber)]">Remove</button>
              </li>
            ))}
          </ul>
          <div className="flex flex-col md:flex-row gap-3">
            <input value={newCondition} onChange={(e) => setNewCondition(e.target.value)}
              placeholder="Add another approval condition…" data-testid="passport-condition-input"
              className="flex-1 bg-[var(--ivc-bg-3)] text-[var(--ivc-ivory)] p-3 text-[14px] border border-[var(--ivc-line-strong)]" />
            <button onClick={addCondition} className="iv-btn iv-btn--ghost" data-testid="passport-condition-add">Add condition</button>
          </div>
          {conditions.length < 2 && (
            <p className="mt-3 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-amber)]">
              At least two conditions are recommended before approval.
            </p>
          )}
        </div>
      )}

      {/* Action band */}
      {rec.status !== "Approved" && (
        <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
            <input type="text" value={approveComment} onChange={(e) => setApproveComment(e.target.value)}
              placeholder="Optional approval note (approver identity is captured automatically)"
              data-testid="passport-approval-note"
              className="w-full bg-transparent text-[var(--ivc-ivory)] border-b border-[var(--ivc-line-strong)] py-2 text-[13.5px]" />
            <div className="flex items-center gap-2 flex-wrap">
              <button className="iv-btn" onClick={() => act("request_revision", { comment: "Please clarify hardware margin contradiction." })} data-testid="record-request-revision">Request revision</button>
              <button className="iv-btn" onClick={() => act("defer")} data-testid="record-defer">Defer</button>
              <button className="iv-btn iv-btn--primary" onClick={() => act("approve", { comment: approveComment, conditions })} data-testid="record-approve">
                Simulate approval <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The Passport view — appears after approval */}
      {rec.status === "Approved" && (
        <PassportView passport={passport} loading={passportLoading} onDownload={async () => {
          const r = await testApi.get("/test/export");
          const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = `decision-passport-${Date.now()}.json`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        }} />
      )}
    </div>
  );
}

function PassportView({ passport, loading, onDownload }) {
  if (loading || !passport) {
    return (
      <div className="iv-canvas rounded-[3px] p-10 text-center" data-testid="passport-loading">
        <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">Generating Decision Passport…</span>
      </div>
    );
  }
  return (
    <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-8" data-testid="passport-view">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="iv-canvas-eyebrow">Decision Passport · Approved · v{passport.approved_version}</div>
        <button onClick={onDownload} className="iv-btn iv-btn--ghost" data-testid="passport-download">
          <Download size={13} /> Download watermarked copy
        </button>
      </div>
      <h3 className="font-serif-display text-[26px] md:text-[32px] leading-tight text-[var(--ivc-ivory)]">
        {passport.opportunity.name}
      </h3>
      <div className="mt-2 flex flex-wrap gap-x-8 gap-y-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
        <span>{passport.opportunity.investment_amount}</span>
        <span>· {passport.opportunity.stage}</span>
        <span>· Next review · {passport.next_review_date?.slice(0, 10)}</span>
      </div>

      <PassportBlock title="Original thesis" body={passport.original_thesis} />
      <PassportBlock title="Recommendation" body={passport.recommendation} />

      <PassportListBlock title="Decisive supporting evidence" items={passport.decisive_supporting_evidence.map(
        (e) => `${e.claim}  —  ${e.source}${e.page ? ", " + e.page : ""}`)} />
      <PassportListBlock title="Material uncertainties" items={passport.material_uncertainties} />
      <PassportListBlock title="Approval conditions" items={passport.approval_conditions} />
      <PassportListBlock title="What must remain true" items={passport.what_must_remain_true} />

      <div className="mt-6 pt-4 border-t border-[var(--ivc-line-strong)]" data-testid="passport-triggers">
        <div className="iv-canvas-eyebrow mb-3">Reconsideration triggers</div>
        <ul className="space-y-3">
          {(passport.reconsideration_triggers || []).map((t) => (
            <li key={t.id} className="grid grid-cols-[auto_1fr] gap-3 items-start">
              <span className="iv-ev-dot iv-ev-dot--review mt-1.5" />
              <div>
                <p className="text-[14px] text-[var(--ivc-ivory)] leading-snug">{t.trigger}</p>
                {t.linked_condition && (
                  <p className="mt-1 font-mono text-[10.5px] tracking-[0.10em] uppercase text-[var(--ivc-gold-2)]">
                    Linked to condition: {t.linked_condition}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 pt-4 border-t border-[var(--ivc-line-strong)]">
        <div className="iv-canvas-eyebrow mb-3">Approval history</div>
        {(passport.approval_history || []).map((a) => (
          <div key={a.id} className="border-l border-[var(--ivc-forest)] pl-4 mb-3">
            <div className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-forest)]">
              Simulated approval · Version {a.record_version}
            </div>
            <div className="text-[13.5px] text-[var(--ivc-ivory)] mt-1">{a.approver_name} &lt;{a.approver_email}&gt;</div>
            <div className="font-mono text-[10.5px] text-[var(--ivc-ivory-3)] mt-0.5">{a.at?.slice(0, 19).replace("T", " ")}</div>
            {a.comment && <p className="text-[13px] text-[var(--ivc-ivory-2)] mt-2">{a.comment}</p>}
          </div>
        ))}
      </div>

      <p className="mt-8 font-mono text-[10.5px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">
        {passport.watermark}
      </p>
    </div>
  );
}

function PassportBlock({ title, body }) {
  return (
    <div className="mt-5 pt-4 border-t border-[var(--ivc-line)] first:border-t-0">
      <div className="iv-canvas-eyebrow mb-2">{title}</div>
      <p className="font-serif-display text-[16px] leading-relaxed text-[var(--ivc-ivory)] max-w-3xl">{body}</p>
    </div>
  );
}
function PassportListBlock({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-5 pt-4 border-t border-[var(--ivc-line)]">
      <div className="iv-canvas-eyebrow mb-3">{title}</div>
      <ul className="space-y-2">
        {items.map((s, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="font-mono text-[10px] tracking-[0.16em] text-[var(--ivc-gold-2)] mt-1.5">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-[14px] text-[var(--ivc-ivory)] leading-snug">{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================ MONITORING */

function MonitoringSurface({ opp, markStep }) {
  const m = opp.monitoring;
  useEffect(() => { markStep("passport"); recordEvent("monitoring_triggers_viewed"); }, []); // eslint-disable-line
  return (
    <div className="space-y-4" data-testid="surface-monitoring">
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
        <div className="iv-canvas-eyebrow mb-3">Monitoring · The decision now knows what to watch.</div>
        <h2 className="font-serif-display text-[28px] md:text-[36px] leading-tight text-[var(--ivc-ivory)]">
          From approval condition to monitoring trigger.
        </h2>
      </div>
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6" data-testid="monitoring-triggers">
        <div className="iv-canvas-eyebrow mb-4">Reconsideration triggers</div>
        <ul className="space-y-4">
          {(m.reconsideration_triggers || []).map((t) => (
            <li key={t.id} className="grid grid-cols-[auto_1fr] gap-4 items-start">
              <span className="iv-ev-dot iv-ev-dot--review mt-2" />
              <div>
                <p className="text-[15px] text-[var(--ivc-ivory)] leading-snug">{t.trigger}</p>
                {t.linked_condition && (
                  <p className="mt-2 font-mono text-[10.5px] tracking-[0.12em] uppercase text-[var(--ivc-gold-2)]">
                    Approval condition → {t.linked_condition}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <MonList title="What must remain true" items={m.what_must_remain_true} />
        <MonList title="Monitoring priorities" items={m.monitoring_priorities} />
        <MonList title="Reporting expectations" items={m.reporting_expectations} />
        <div>
          <div className="iv-canvas-eyebrow mb-2">Named owners</div>
          <ul className="space-y-1.5">
            {(m.named_owners || []).map((o) => (
              <li key={o.role} className="text-[13.5px] text-[var(--ivc-ivory)]">
                <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] mr-2">{o.role}</span>
                {o.name}
              </li>
            ))}
          </ul>
          <div className="iv-canvas-eyebrow mt-6 mb-2">Next review</div>
          <p className="text-[14.5px] text-[var(--ivc-ivory)]">{m.next_review_date?.slice(0, 10)}</p>
        </div>
      </div>
      <div className="iv-canvas rounded-[3px] px-6 md:px-10 py-6">
        <div className="iv-canvas-eyebrow mb-4">Milestones</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {m.milestones.map((ms) => (
            <div key={ms.quarter} className="border border-[var(--ivc-line-warm)] p-4">
              <div className="font-mono text-[10.5px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">{ms.quarter}</div>
              <div className="text-[13px] text-[var(--ivc-ivory)] mt-2 leading-snug">{ms.focus}</div>
              {ms.owner && <div className="mt-2 font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--ivc-ivory-3)]">Owner · {ms.owner}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MonList({ title, items }) {
  return (
    <div>
      <div className="iv-canvas-eyebrow mb-2">{title}</div>
      <ul className="space-y-1.5">{(items || []).map((s, i) => <li key={i} className="text-[13.5px] text-[var(--ivc-ivory)] leading-snug">• {s}</li>)}</ul>
    </div>
  );
}

/* ============================================================ FEEDBACK */

function FeedbackModal({ onClose }) {
  const [f, setF] = useState({ would_help: "", most_valuable: "", obstacle: "", would_test_live: null,
                                first_opportunity_type: "", may_contact: null });
  const submit = async () => {
    if (!f.would_help) return;
    try { await testApi.post("/test/feedback", f); toast.success("Thank you — feedback recorded."); onClose(); }
    catch { toast.error("Unable to record feedback."); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(11,13,12,0.72)] backdrop-blur-sm">
      <div className="iv-canvas rounded-[3px] max-w-xl w-full p-8" data-testid="demo-feedback-modal">
        <div className="iv-canvas-eyebrow mb-3">Optional feedback</div>
        <h3 className="font-serif-display text-[22px] text-[var(--ivc-ivory)] leading-tight">
          Would this improve how your Office prepares and preserves investment decisions?
        </h3>
        <div className="mt-5 flex gap-2 flex-wrap">
          {["yes","possibly","not_yet"].map((v) => (
            <button key={v} onClick={() => setF((x) => ({ ...x, would_help: v }))}
              className={`iv-btn ${f.would_help === v ? "iv-btn--primary" : ""}`}
              data-testid={`feedback-${v}`}>{v.replace("_"," ")}</button>
          ))}
        </div>
        <label className="iv-field mt-6"><span>What part was most valuable?</span>
          <input value={f.most_valuable} onChange={(e) => setF((x) => ({ ...x, most_valuable: e.target.value }))} placeholder="Optional" /></label>
        <label className="iv-field mt-4"><span>What would prevent your Office from using this?</span>
          <input value={f.obstacle} onChange={(e) => setF((x) => ({ ...x, obstacle: e.target.value }))} placeholder="Optional" /></label>
        <label className="iv-field mt-4"><span>Would you consider using ImpactVest with one live opportunity?</span>
          <select value={f.would_test_live == null ? "" : String(f.would_test_live)} onChange={(e) => setF((x) => ({ ...x, would_test_live: e.target.value === "" ? null : e.target.value === "true" }))}>
            <option value="">—</option><option value="true">Yes</option><option value="false">Not yet</option>
          </select>
        </label>
        <label className="iv-field mt-4"><span>What type of opportunity would you use first?</span>
          <input value={f.first_opportunity_type} onChange={(e) => setF((x) => ({ ...x, first_opportunity_type: e.target.value }))} placeholder="Optional" /></label>
        <div className="mt-6 flex items-center gap-3">
          <label className="text-[13px] text-[var(--ivc-ivory-2)]">
            <input type="checkbox" className="iv-checkbox mr-2" checked={!!f.may_contact} onChange={(e) => setF((x) => ({ ...x, may_contact: e.target.checked }))} />
            May we contact you about organizational access?
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="iv-btn iv-btn--ghost">Later</button>
          <button onClick={submit} className="iv-btn iv-btn--primary" data-testid="feedback-submit">Submit</button>
        </div>
      </div>
    </div>
  );
}
